import React, { useState, useEffect } from "react";
import { Tabs, Layout, Typography, Card, Badge, Breadcrumb, Space, Button, Tooltip } from "antd";
import { 
  ThunderboltOutlined, 
  HistoryOutlined, 
  GatewayOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  SettingOutlined,
  DashboardOutlined
} from "@ant-design/icons";
import StreamPage from "./StreamPage";
import HistoryPage from "./HistoryPage";
import GatewayListPage from "./GatewayListPage";
import GatewayDetail from "../components/GatewayDetail";
import Logout from "../components/Logout";
import { hasAnyPermission, useAuth } from "../services/auth";

const { Content, Header } = Layout;
const { Title, Text } = Typography;

const DeviceSettingPage = () => {
  const [tab, setTab] = useState("stream");
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyCache, setHistoryCache] = useState(null);
  const [user] = useAuth();
  const canViewIot = hasAnyPermission(user, [
    "device_view",
    "device_edit",
    "device_manage",
    "warehouse_view",
    "warehouse_edit",
    "warehouse_manage",
  ]);

  // Animation effect when switching tabs
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 30);
    return () => clearTimeout(timer);
  }, [tab]);
  if (!canViewIot) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Title level={3} type="danger">Bạn không có quyền truy cập trang này</Title>
        <div>Vui lòng liên hệ quản trị viên nếu bạn cần quyền truy cập.</div>
      </div>
    );
  }
  const handleTabChange = (newTab) => {
    if (newTab !== "gateway") {
      setSelectedGateway(null);
    }
    setTab(newTab);
  };

  const handleGatewaySelect = (gateway) => {
    setSelectedGateway(gateway);
  };

  const handleBackToGatewayList = () => {
    setSelectedGateway(null);
  };

  const getTabItems = () => [
    {
      key: "stream",
      label: (
        <Space>
          <ThunderboltOutlined />
          <span>Events Streaming</span>
          <Badge 
            count="LIVE" 
            style={{ 
              backgroundColor: '#52c41a',
              fontSize: '10px',
              height: '18px',
              lineHeight: '18px',
              borderRadius: '9px'
            }} 
          />
        </Space>
      ),
      children: (
        <div className="tab-content-wrapper">
          <StreamPage />
        </div>
      ),
    },
    {
      key: "history",
      label: (
        <Space>
          <HistoryOutlined />
          <span>Events History</span>
        </Space>
      ),
      children: (
        <div className="tab-content-wrapper">
          <HistoryPage cache={historyCache} setCache={setHistoryCache} />
        </div>
      ),
    },
    {
      key: "gateway",
      label: (
        <Space>
          <GatewayOutlined />
          <span>Gateway Management</span>
          {selectedGateway && (
            <Badge 
              count={1} 
              style={{ backgroundColor: '#1890ff' }}
            />
          )}
        </Space>
      ),
      children: (
        <div className="tab-content-wrapper">
          {selectedGateway ? (
            <div>
              {/* <div style={{ marginBottom: 16 }}>
                <Breadcrumb
                  items={[
                    {
                      title: (
                        <Space>
                          <HomeOutlined />
                          <span>Gateway List</span>
                        </Space>
                      ),
                    },
                    {
                      title: (
                        <Space>
                          <GatewayOutlined />
                          <span>{selectedGateway.name || selectedGateway.id || 'Gateway Detail'}</span>
                        </Space>
                      ),
                    },
                  ]}
                />
                <Button
                  type="primary"
                  ghost
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToGatewayList}
                  style={{ marginTop: 8 }}
                >
                  Back to Gateway List
                </Button>
              </div> */}
              <GatewayDetail
                gateway={selectedGateway}
                onBack={handleBackToGatewayList}
              />
            </div>
          ) : (
            <GatewayListPage onSelect={handleGatewaySelect} />
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Custom CSS */}
      <style jsx>{`
        .device-setting-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 0 0 16px 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          position: relative;
          z-index: 1;
        }
        
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 16px;
          height: 100%;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .header-title {
          color: white !important;
          margin: 0 !important;
          font-weight: 600;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          font-size: 20px;
        }
        
        .header-subtitle {
          color: rgba(255, 255, 255, 0.8) !important;
          margin: 0 !important;
          font-size: 12px;
        }
        
        .main-content {
          margin: 16px;
          padding: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .header-content {
            padding: 0 12px;
            flex-direction: column;
            gap: 8px;
            text-align: center;
          }
          
          .header-title {
            font-size: 18px;
          }
          
          .header-subtitle {
            font-size: 11px;
          }
          
          .main-content {
            margin: 12px;
          }
          
          .stats-card {
            margin-bottom: 12px;
          }
        }
        
        @media (max-width: 480px) {
          .header-content {
            padding: 0 8px;
          }
          
          .header-title {
            font-size: 16px;
          }
          
          .header-subtitle {
            display: none;
          }
          
          .main-content {
            margin: 8px;
          }
        }
        
        @media (min-width: 1200px) {
          .header-content {
            padding: 0 32px;
          }
          
          .main-content {
            margin: 24px auto;
            max-width: 1200px;
          }
        }
        
        .tabs-wrapper {
          background: white;
        }
        
        .tab-content-wrapper {
          padding: 16px;
          min-height: 500px;
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @media (min-width: 768px) {
          .tab-content-wrapper {
            padding: 24px;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .ant-tabs-tab {
          font-weight: 500;
          padding: 12px 20px !important;
        }
        
        .ant-tabs-tab-active {
          background: linear-gradient(135deg, #667eea20 0%, #764ba220 100%);
          border-radius: 8px 8px 0 0;
        }
        
        .ant-tabs-content-holder {
          background: #fafafa;
        }
        
        .stats-card {
          margin-bottom: 16px;
        }
        
        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
      `}</style>

      {/* Header */}
      <Header className="device-setting-header" style={{ height: 'auto', minHeight: '70px', padding: '12px 0' }}>
        <div className="header-content">
          <div>
            <Title level={3} className="header-title">
              <Space size="small">
                <SettingOutlined />
                <span className="header-title-text">Device Management</span>
              </Space>
            </Title>
            <Text className="header-subtitle">
              Real-time monitoring and gateway configuration
            </Text>
          </div>
          <div>
            <Logout />
          </div>
        </div>
      </Header>

      {/* Main Content */}
      <Content>
        <div className="main-content">
          {/* Tabs */}
          <div className="tabs-wrapper" style={{ position: 'relative' }}>
            {isLoading && (
              <div className="loading-overlay">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 8px'
                  }} />
                  <Text type="secondary">Loading...</Text>
                </div>
              </div>
            )}
            
            <Tabs
              activeKey={tab}
              onChange={handleTabChange}
              items={getTabItems()}
              size="large"
              tabBarStyle={{
                margin: 0,
                padding: '0 16px',
                background: 'white',
                borderBottom: '1px solid #f0f0f0'
              }}
              animated={{
                inkBar: true,
                tabPane: true,
              }}
            />
          </div>
        </div>
      </Content>

      {/* Additional CSS for animations */}
      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ant-tabs-content {
          background: #fafafa;
        }
        
        .ant-tabs-tab:hover {
          background: rgba(102, 126, 234, 0.05);
          border-radius: 8px 8px 0 0;
          transition: all 0.3s ease;
        }
        
        .ant-badge-count {
          font-size: 10px;
        }
      `}</style>
    </Layout>
  );
};

export default DeviceSettingPage;
